(() => {
  if (customElements.get('kidsbazar-reviews')) return;

  class KidsBazarReviews extends HTMLElement {
    connectedCallback() {
      this.destroy();
      this.viewport = this.querySelector('.reviews__viewport');
      this.track = this.querySelector('.reviews__grid');
      this.speed = Number(this.dataset.speed) || 28;
      this.enabled = this.dataset.autoSlide === 'true';
      this.paused = false;
      this.dragging = false;
      this.inViewport = false;
      this.frame = 0;
      this.loopWidth = 0;
      this.canAutoSlide = false;
      if (!this.viewport || !this.track) return;

      const cards = Array.from(this.track.children).filter((node) => !node.hasAttribute('data-reviews-clone'));
      this.canAutoSlide = this.enabled && cards.length >= 2;
      this.track.querySelectorAll('[data-reviews-clone]').forEach((node) => node.remove());

      if (this.enabled && cards.length >= 2) {
        cards.forEach((card) => {
          const clone = card.cloneNode(true);
          clone.setAttribute('data-reviews-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
          this.track.append(clone);
        });
      }
      this.firstOriginal = cards[0] || null;
      this.firstClone = this.track.querySelector('[data-reviews-clone]');

      // Drag with a mouse on desktop. Leave native touch scrolling untouched.
      this.onPointerDown = (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        this.dragging = true;
        this.paused = true;
        this.stopLoop();
        this.dragStartX = event.clientX;
        this.dragStartScroll = this.viewport.scrollLeft;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      };
      this.onPointerMove = (event) => {
        if (!this.dragging) return;
        const distance = event.clientX - this.dragStartX;
        this.viewport.scrollLeft = this.dragStartScroll - distance;
        if (Math.abs(distance) > 3) event.preventDefault();
      };
      this.onPointerUp = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.paused = false;
        this.viewport.classList.remove('is-dragging');
        this.startLoop();
      };
      this.onFocus = () => {
        this.paused = true;
        this.stopLoop();
      };
      this.onBlur = () => {
        if (!this.dragging) {
          this.paused = false;
          this.startLoop();
        }
      };

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('lostpointercapture', this.onPointerUp);
      this.viewport.addEventListener('focusin', this.onFocus);
      this.viewport.addEventListener('focusout', this.onBlur);

      this.lastTime = 0;
      this.loop = (time) => {
        this.frame = 0;
        if (!this.isConnected || !this.viewport || !this.track || !this.inViewport || document.hidden || !this.canAutoSlide || this.paused || this.dragging) return;
        if (!this.lastTime) this.lastTime = time;
        const delta = Math.min(time - this.lastTime, 50);
        this.lastTime = time;

        // Measure the repeated-track width only when needed, not on every animation frame.
        if (!this.loopWidth && this.firstOriginal && this.firstClone) {
          this.loopWidth = this.firstClone.getBoundingClientRect().left - this.firstOriginal.getBoundingClientRect().left;
        }
        if (this.loopWidth > 0) {
          this.viewport.scrollLeft += this.speed * delta / 1000;
          if (this.viewport.scrollLeft >= this.loopWidth) this.viewport.scrollLeft %= this.loopWidth;
        }
        this.frame = requestAnimationFrame(this.loop);
      };

      this._visibilityObserver = new IntersectionObserver((entries) => {
        const inViewport = entries.some((entry) => entry.isIntersecting);
        this.inViewport = inViewport;
        if (inViewport) this.startLoop();
        else this.stopLoop();
      }, { rootMargin: '120px 0px' });
      this._visibilityObserver.observe(this);

      this._onDocumentVisibility = () => {
        if (document.hidden) this.stopLoop();
        else this.startLoop();
      };
      document.addEventListener('visibilitychange', this._onDocumentVisibility);

      this.onResize = () => {
        this.loopWidth = 0;
        if (this.inViewport) {
          this.stopLoop();
          this.startLoop();
        }
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      this.startLoop();
    }

    startLoop() {
      if (!this.loop || !this.canAutoSlide || !this.inViewport || document.hidden || this.paused || this.dragging || this.frame) return;
      this.lastTime = 0;
      this.frame = requestAnimationFrame(this.loop);
    }

    stopLoop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastTime = 0;
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      this.stopLoop();
      if (this._visibilityObserver) this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
      if (this._onDocumentVisibility) document.removeEventListener('visibilitychange', this._onDocumentVisibility);
      this._onDocumentVisibility = null;
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      this.onResize = null;
      if (this.viewport) {
        if (this.onPointerDown) this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        if (this.onPointerMove) this.viewport.removeEventListener('pointermove', this.onPointerMove);
        if (this.onPointerUp) {
          this.viewport.removeEventListener('pointerup', this.onPointerUp);
          this.viewport.removeEventListener('pointercancel', this.onPointerUp);
          this.viewport.removeEventListener('lostpointercapture', this.onPointerUp);
        }
        if (this.onFocus) this.viewport.removeEventListener('focusin', this.onFocus);
        if (this.onBlur) this.viewport.removeEventListener('focusout', this.onBlur);
        this.viewport.classList.remove('is-dragging');
      }
      if (this.track) this.track.querySelectorAll('[data-reviews-clone]').forEach((node) => node.remove());
      this.frame = null;
      this.viewport = null;
      this.track = null;
      this.onPointerDown = null;
      this.onPointerMove = null;
      this.onPointerUp = null;
      this.onFocus = null;
      this.onBlur = null;
      this.firstOriginal = null;
      this.firstClone = null;
      this.loopWidth = 0;
      this.inViewport = false;
      this.canAutoSlide = false;
      this.dragging = false;
    }
  }

  customElements.define('kidsbazar-reviews', KidsBazarReviews);
})();